import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../../web/ReactQueryProvider';

const ZOOM_CONNECTION_KEY = 'scheduler/zoom/connection';

/**
 * Fetches a user's Zoom connection status for the Scheduler. `GET /scheduler/zoom/connection` → { connected, email }.
 *
 * Pass `forUserId` to look up another (same-account) user's status — used when an account admin configures a meeting
 * type on behalf of that user. The backend authorizes this via the account-admin-for-user mechanism and returns 403
 * for non-privileged callers, so callers MUST only set `forUserId` when the current user is an account admin (or has
 * send-on-behalf for the target); omit it otherwise to query the caller's own status.
 */
export function useZoomConnectionQuery({
	enabled = true,
	forUserId,
	impersonationContext,
	meta,
}: {
	enabled?: boolean;
	forUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
	meta?: LevStandardQueryMeta;
} = {}) {
	const userSession = useUserSession();
	return useQuery({
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.IZoomConnectionStatus>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { forUserId },
					urlPath: ZOOM_CONNECTION_KEY,
				}),
				'GET'
			),
		// Refetch on window focus so the status stays fresh if the connection changes out-of-band (e.g. disconnected
		// in another tab) while a long-lived surface like the meeting-config modal is open.
		// Key off primitive ids rather than the impersonationContext object, so a freshly-constructed context object on
		// each render (a documented mutation footgun) doesn't churn the key and force refetches. Anchor on the session
		// user id too, so a user switch in the same tab can't surface the previous user's cached status.
		queryKey: [
			ZOOM_CONNECTION_KEY,
			userSession.user?.id,
			impersonationContext?.account?.id,
			impersonationContext?.user?.id,
			forUserId,
		],
		enabled,
		meta,
	});
}

export const invalidateZoomConnectionQuery = () =>
	reactQueryClient.invalidateQueries({ queryKey: [ZOOM_CONNECTION_KEY] });
